import { useEffect, useRef, type JSX } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useUserDataStore } from '../stateStores/User'
import { identifyUser, resetPosthogUser } from './posthogUser'
import { posthogClient } from '../posthogClient'
import { UserTenantMembershipDto } from '../api/tenant/models/UserDto'

const buildName = (opts: {
    explicitName?: string
    email?: string
    person?: { firstName?: string; lastName?: string }
}): string | undefined => {
    if (opts.explicitName && opts.explicitName.trim()) return opts.explicitName

    const personName = [opts.person?.firstName, opts.person?.lastName].filter(
        Boolean,
    )

    if (personName.length) return personName.join(' ')

    return opts.email
}

const rolesForTenant = (
    memberships: UserTenantMembershipDto[] | undefined,
    tenantId: string | undefined,
): string[] | undefined => {
    if (!tenantId || !memberships) return undefined
    const membership = memberships.find((m) => m.tenantId === tenantId)
    return membership?.roles
}

/**
 * Keeps PostHog user/tenant identity in sync with our auth + tenant state.
 */
export function PosthogIdentitySync(): JSX.Element | null {
    const { user: authUser } = useAuth()
    const userData = useUserDataStore((state) => state.userData)

    const lastSignatureRef = useRef<string | null>(null)
    const lastUserIdRef = useRef<string | null>(null)

    useEffect(() => {
        if (!posthogClient) return

        const userId = authUser?.id ?? null
        const tenantId = userData?.tenant.id ?? undefined
        const tenantName = userData?.tenant.name ?? undefined

        const email = userData?.user.email ?? authUser?.email
        const name = buildName({
            explicitName: userData?.user.name,
            email,
            person: userData?.user.person,
        })
        const roles = rolesForTenant(userData?.user.memberships, tenantId)
        const isProductAdmin = userData?.user.isProductAdmin ?? false

        const signature = [
            userId ?? '',
            email ?? '',
            name ?? '',
            roles?.join(',') ?? '',
            tenantId ?? '',
            tenantName ?? '',
            isProductAdmin ? 'admin' : '',
        ].join('|')

        if (!userId) {
            if (lastUserIdRef.current || lastSignatureRef.current) {
                resetPosthogUser()
            }
            lastUserIdRef.current = null
            lastSignatureRef.current = null
            return
        }

        if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
            resetPosthogUser()
        }

        if (signature === lastSignatureRef.current) {
            lastUserIdRef.current = userId
            return
        }

        identifyUser({
            id: userId,
            email,
            name,
            roles,
            tenantId: tenantId ?? undefined,
            tenantName: tenantName ?? undefined,
            isProductAdmin,
        })

        lastSignatureRef.current = signature
        lastUserIdRef.current = userId
    }, [authUser, userData])

    return null
}
